/**
 * Task 151-B — package create/edit drawer. The real package-management
 * surface from the reference: basic package fields + a service
 * accordion whose enabled rows reveal the per-kind config section. Saves
 * the shell via `upsertAgencyPackage` and the composition + per-line
 * `config` via `setAgencyPackageServices` (the 151-A persistence path).
 *
 * The per-service `config` round-trips through 151-A's `config jsonb`:
 * loaded packages hydrate their typed config into drawer state
 * (`buildDrawerLines`), and saving serializes it back
 * (`buildSetServicesInput`). The V2 typed commercial columns
 * (pricing_model / retainer / spend% / cadence / quantity / deliverables
 * / terms_notes / sort_order) stay editable via the per-service
 * `AdvancedTermsSection`; the `included` scope toggle is surfaced in the
 * main flow (Task 280I). For ads-kind lines the legacy pricing columns
 * are derived from config at save time so they cannot diverge.
 */

import { useMemo, useState } from 'react';
import { setAgencyPackageServices, upsertAgencyPackage } from '../../api/endpoints';
import type { AgencyPackageRow, AgencyServiceRow, PackageBillingInterval } from '../../api/types';
import { AdvancedTermsSection } from './AdvancedTermsSection';
import { DeliverablesEditor } from './DeliverablesEditor';
import { Drawer } from './Drawer';
import { SimpleField, SimpleSelect } from './formPrimitives';
import { billingIntervalLabel, handleApiErr } from './helpers';
import { InfluencerScopeSection } from './InfluencerScopeSection';
import {
  buildDrawerLines,
  buildSetServicesInput,
  type DrawerLineState,
} from './packageDrawerModel';
import { hasMeaningfulConfig, normalizeConfig, validateConfig } from './packageServiceConfigModel';
import {
  automationBadgeForFamily,
  familyForKind,
  familyLayout,
  PACKAGE_SERVICE_FAMILY_LABELS_AR,
} from './packageServiceFamily';
import { isPackageServiceLineVisible } from './launchVisibility';
import { ServiceConfigSection } from './serviceConfigSections';
import { ToggleRow } from './serviceConfigSections/primitives';

const BILLING_OPTIONS: ReadonlyArray<PackageBillingInterval> = [
  'monthly',
  'quarterly',
  'yearly',
  'custom',
];

export function PackageDrawer({
  pkg,
  services,
  onClose,
  onSaved,
}: {
  /** `null` = create a new package; otherwise edit this one. */
  readonly pkg: AgencyPackageRow | null;
  readonly services: ReadonlyArray<AgencyServiceRow>;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const isEdit = pkg !== null;

  // Toggleable services = active ones, plus any inactive service already
  // in the package (so editing never silently drops it on save).
  const drawerServices = useMemo(() => {
    const active = services.filter((s) => s.is_active);
    if (!pkg) return active;
    const activeIds = new Set(active.map((s) => s.id));
    const includedInactive = services.filter(
      (s) => !activeIds.has(s.id) && pkg.services.some((l) => l.service_id === s.id),
    );
    return [...active, ...includedInactive];
  }, [services, pkg]);

  const [name, setName] = useState(pkg?.name ?? '');
  const [description, setDescription] = useState(pkg?.description ?? '');
  const [price, setPrice] = useState(pkg?.price_amount != null ? String(pkg.price_amount) : '');
  const [currency] = useState(pkg?.price_currency ?? 'SAR');
  const [billing, setBilling] = useState<PackageBillingInterval>(
    pkg?.billing_interval ?? 'monthly',
  );
  const [isActive, setIsActive] = useState(pkg?.is_active ?? true);
  const [lines, setLines] = useState<ReadonlyArray<DrawerLineState>>(() =>
    buildDrawerLines(drawerServices, pkg),
  );

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const visibleLines = lines.filter(isPackageServiceLineVisible);
  const visibleEnabledCount = visibleLines.filter((l) => l.enabled).length;
  const nameError = name.trim().length === 0;
  const priceError = price.trim() !== '' && !Number.isFinite(Number(price));

  function setLine(id: string, fn: (l: DrawerLineState) => DrawerLineState) {
    setLines((ls) => ls.map((l) => (l.service_id === id ? fn(l) : l)));
  }

  async function save() {
    if (nameError) {
      setError('اسم الباقة مطلوب.');
      return;
    }
    if (priceError) {
      setError('السعر يجب أن يكون رقماً.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const priceNum = price.trim() === '' ? null : Number(price);
      const res = await upsertAgencyPackage({
        input: {
          ...(pkg ? { id: pkg.id } : {}),
          name: name.trim(),
          description: description.trim() === '' ? null : description.trim(),
          price_amount: priceNum !== null && Number.isFinite(priceNum) ? priceNum : null,
          price_currency: currency,
          billing_interval: billing,
          is_active: isActive,
        },
      });
      await setAgencyPackageServices({
        input: buildSetServicesInput(res.package.id, lines),
      });
      onSaved();
    } catch (err) {
      setSubmitting(false);
      handleApiErr(err, setError);
    }
  }

  return (
    <Drawer
      eyebrow={isEdit ? 'تعديل باقة' : 'باقة جديدة'}
      title={pkg ? `تعديل · ${pkg.name}` : 'إنشاء باقة جديدة'}
      onClose={onClose}
      busy={submitting}
      footer={
        <>
          <button
            type="button"
            className="btn-text"
            onClick={onClose}
            disabled={submitting}
            style={{ padding: '10px 14px' }}
          >
            إلغاء
          </button>
          <button
            type="button"
            className="auth-cta"
            onClick={save}
            disabled={submitting || nameError}
            style={{ width: 'auto' }}
          >
            {submitting ? <span className="spinner" /> : null}
            <span>{submitting ? 'جاري الحفظ…' : isEdit ? 'حفظ التغييرات' : 'إنشاء الباقة'}</span>
          </button>
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Basic info */}
        <SimpleField
          label="اسم الباقة"
          value={name}
          required
          maxLength={120}
          placeholder="مثال: الباقة الذهبية"
          onChange={setName}
        />
        <SimpleField
          label="الوصف"
          value={description}
          textarea
          maxLength={500}
          placeholder="وصف مختصر يساعد فريقك على اختيار الباقة الصحيحة"
          onChange={setDescription}
        />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <SimpleField
            label={`السعر (${currency})`}
            value={price}
            type="number"
            dirLtr
            placeholder="0"
            onChange={setPrice}
          />
          <SimpleSelect
            label="نوع الفوترة"
            value={billing}
            onChange={(v) => setBilling(v as PackageBillingInterval)}
            options={BILLING_OPTIONS.map((b) => ({ value: b, label: billingIntervalLabel(b) }))}
          />
        </div>
        <ToggleRow
          label="باقة نشطة"
          desc="الباقات النشطة فقط تظهر عند ربطها بعميل جديد."
          value={isActive}
          disabled={submitting}
          onChange={setIsActive}
        />

        <div style={{ height: 1, background: 'var(--line)', margin: '2px 0' }} />

        {/* Service accordion */}
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <span className="lbl" style={{ fontSize: 11, color: 'var(--ink-2)' }}>
            الخدمات المضمنة
          </span>
          <span className="tiny" style={{ color: 'var(--muted)' }}>
            {visibleEnabledCount} من {visibleLines.length} مفعّلة
          </span>
        </div>

        {visibleLines.length === 0 ? (
          <p className="tiny" style={{ color: 'var(--muted)' }}>
            لا توجد خدمات نشطة بعد. أنشئ خدمات أولاً من قسم «إدارة الكتالوج» بالأسفل.
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {visibleLines.map((line) => {
              // Task 161 — sellable family view: derive the agency-friendly
              // family + automation badge from the backend kind AND the
              // service capability. Task 161B — per-family layout dispatch
              // replaces the boolean "automated vs not" flag: the typed
              // families (social_media / paid_campaigns) lead with the typed
              // sub-config (no generic deliverables rows — the quotas /
              // pricing ARE the deliverables); the influencer family leads
              // with a minimal scope + workflow-later block (no count /
              // category UX, no deliverables rows); content_production /
              // custom keep the generic deliverables editor as primary.
              const family = familyForKind(line.kind, line.system_capability_key);
              const familyLabel = PACKAGE_SERVICE_FAMILY_LABELS_AR[family];
              const automationBadge = automationBadgeForFamily(family);
              const layout = familyLayout(family);
              // Task 161B (Codex round-1 P2) — `validateConfig('influencer')`
              // requires `count > 0`, but the influencer-scope layout HIDES
              // count from both main and advanced editing (the spec rules
              // it out as a "fake workflow field"). Running the validator
              // here would show an unresolvable "حدّد عدد المؤثرين" hint
              // the user has no way to fix. Skip validation for that mode
              // only — the other families still validate the fields the
              // user CAN edit.
              const normalizedConfig = normalizeConfig(line.kind, line.config);
              const issues =
                line.enabled && layout.mainFlow !== 'influencer-scope'
                  ? validateConfig(line.kind, normalizedConfig)
                  : [];
              // Task 280F (Phase 1A) — warn when a service is configured but not
              // included: unincluded services grant no client capability and are
              // skipped by content-gen (Task 280B).
              const configuredButNotIncluded =
                line.enabled && !line.included && hasMeaningfulConfig(line.kind, normalizedConfig);
              return (
                <div
                  key={line.service_id}
                  className="card"
                  style={{
                    padding: 0,
                    overflow: 'hidden',
                    borderColor: line.enabled ? 'var(--tangerine)' : 'var(--line-2)',
                  }}
                >
                  <button
                    type="button"
                    aria-expanded={line.enabled}
                    disabled={submitting}
                    onClick={() => setLine(line.service_id, (l) => ({ ...l, enabled: !l.enabled }))}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '12px 14px',
                      border: 'none',
                      background: line.enabled ? 'var(--tangerine-soft)' : 'transparent',
                      textAlign: 'start',
                      cursor: submitting ? 'not-allowed' : 'pointer',
                    }}
                  >
                    <span
                      aria-hidden="true"
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 5,
                        border: `2px solid ${line.enabled ? 'var(--tangerine)' : 'var(--line-3)'}`,
                        background: line.enabled ? 'var(--tangerine)' : 'transparent',
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11,
                        flexShrink: 0,
                      }}
                    >
                      {line.enabled ? '✓' : ''}
                    </span>
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>
                      {line.service_name}
                    </span>
                  </button>

                  {line.enabled ? (
                    <div
                      style={{
                        padding: 16,
                        borderTop: '1px solid var(--line)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 14,
                      }}
                    >
                      {/* Family header — what the agency is actually selling
                          and how delivery consumption is tracked today. The
                          backend `kind` discriminator is NO LONGER editable
                          from the UI (Task 166B removed the override
                          dropdown from advanced); it is decided at creation
                          time by the capability and round-trips through
                          `serializeConfig(l.kind, ...)`. To use a different
                          type, add a separate service line. */}
                      <div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            flexWrap: 'wrap',
                          }}
                        >
                          <span className="lbl" style={{ fontSize: 11, color: 'var(--ink-2)' }}>
                            {familyLabel}
                          </span>
                          <span
                            className="chip"
                            data-automation-tone={automationBadge.tone}
                            style={{
                              fontSize: 11,
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: 'var(--cream-2)',
                              border: '1px solid var(--line)',
                            }}
                          >
                            {automationBadge.label}
                          </span>
                        </div>
                        <p className="tiny" style={{ color: 'var(--muted)', margin: '4px 0 0 0' }}>
                          {automationBadge.description}
                        </p>
                      </div>

                      {/* Task 280I — surface the "included in scope" flag in the
                          MAIN flow. It used to live only inside the collapsed
                          advanced section, yet the configured-but-not-included
                          warning below tells the manager to toggle it — so the
                          control the warning references was hidden. Reads/writes
                          the same `line.included` state: the default stays true
                          (model) and a manual "off" is preserved. */}
                      <ToggleRow
                        label="مضمّنة في نطاق الباقة"
                        desc="الخدمات غير المُضمّنة لا تُحتسب ضمن قدرات العميل ولا تخطيط المحتوى."
                        value={line.included}
                        disabled={submitting}
                        onChange={(v) => setLine(line.service_id, (l) => ({ ...l, included: v }))}
                      />

                      {/* Task 161B — main flow is family-dispatched. For the
                          typed families (social_media / paid_campaigns) the
                          typed config IS the deliverable definition, so we
                          render ONLY `ServiceConfigSection` (no duplicated
                          generic deliverables rows). For the influencer
                          family we render the minimal scope-with-promise
                          block. For content_production / custom we keep the
                          generic `DeliverablesEditor` as the primary UX
                          since each deliverable is a discrete production
                          output and there is no typed sub-config to lead
                          with. */}
                      {layout.mainFlow === 'typed-config' ? (
                        <ServiceConfigSection
                          kind={line.kind}
                          config={line.config}
                          disabled={submitting}
                          onChange={(raw) =>
                            setLine(line.service_id, (l) => ({ ...l, config: raw }))
                          }
                        />
                      ) : layout.mainFlow === 'influencer-scope' ? (
                        <InfluencerScopeSection
                          config={line.config}
                          disabled={submitting}
                          onChange={(raw) =>
                            setLine(line.service_id, (l) => ({ ...l, config: raw }))
                          }
                        />
                      ) : (
                        <div>
                          <div
                            className="lbl"
                            style={{ marginBottom: 4, fontSize: 12, fontWeight: 600 }}
                          >
                            مخرجات الباقة
                          </div>
                          <DeliverablesEditor
                            deliverables={line.deliverables}
                            submitting={submitting}
                            onChange={(d) =>
                              setLine(line.service_id, (l) => ({ ...l, deliverables: d }))
                            }
                          />
                        </div>
                      )}
                      {issues.length > 0 ? (
                        <ul
                          style={{
                            listStyle: 'none',
                            padding: 0,
                            margin: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 4,
                          }}
                        >
                          {issues.map((msg, idx) => (
                            <li key={idx} className="tiny" style={{ color: 'var(--muted)' }}>
                              • {msg}
                            </li>
                          ))}
                        </ul>
                      ) : null}

                      {/* Task 161B (Codex round-2 P1) — legacy deliverables
                          cleanup surface. Shown ONLY when this family does NOT
                          lead with the deliverables editor AND the line
                          actually carries persisted rows. Silently dropping
                          them on save would be data loss; surfacing them here
                          gives the manager a visible cleanup path (per-row
                          edit + a "مسح الكل" shortcut), AND a "no surprise"
                          path for the rare case where the rows are still
                          meaningful and should stay. The save path mirrors
                          drawer state — clearing here clears on save. */}
                      {layout.mainFlow !== 'deliverables' && line.deliverables.length > 0 ? (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            padding: 10,
                            border: '1px dashed var(--line-2)',
                            borderRadius: 'var(--r-md)',
                            background: 'var(--cream-2)',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'baseline',
                              justifyContent: 'space-between',
                              gap: 8,
                              flexWrap: 'wrap',
                            }}
                          >
                            <div style={{ minWidth: 0 }}>
                              <div className="lbl" style={{ fontSize: 12, fontWeight: 600 }}>
                                تسليمات قديمة
                              </div>
                              <p
                                className="tiny"
                                style={{
                                  color: 'var(--muted)',
                                  margin: '2px 0 0 0',
                                  lineHeight: 1.5,
                                }}
                              >
                                هذه التسليمات محفوظة من نسخة سابقة للواجهة. هذا الإصدار يعتمد على
                                إعدادات الخدمة كمصدر للمخرجات، فراجِعها واحذف ما لم يعد مناسباً.
                              </p>
                            </div>
                            <button
                              type="button"
                              className="btn-text"
                              disabled={submitting}
                              onClick={() =>
                                setLine(line.service_id, (l) => ({ ...l, deliverables: [] }))
                              }
                              style={{ padding: '4px 8px', fontSize: 12, color: 'var(--rose)' }}
                            >
                              مسح الكل
                            </button>
                          </div>
                          <DeliverablesEditor
                            deliverables={line.deliverables}
                            submitting={submitting}
                            onChange={(d) =>
                              setLine(line.service_id, (l) => ({ ...l, deliverables: d }))
                            }
                          />
                        </div>
                      ) : null}

                      {configuredButNotIncluded ? (
                        <div
                          style={{
                            padding: '10px 12px',
                            border: '1px solid var(--tangerine)',
                            borderRadius: 'var(--r-md)',
                            background: 'var(--tangerine-soft)',
                          }}
                        >
                          <p
                            className="tiny"
                            style={{ margin: 0, color: 'var(--ink-2)', lineHeight: 1.6 }}
                          >
                            هذه الخدمة مُعدّة بقيم لكنها غير مُضمّنة في الباقة. الخدمات غير
                            المُضمّنة لا تُفعّل قدرات العميل ولا تدخل في تخطيط المحتوى. فعّل «مضمّنة
                            في نطاق الباقة» إذا كانت جزءًا من عمل العميل.
                          </p>
                        </div>
                      ) : null}

                      <AdvancedTermsSection
                        line={line}
                        family={family}
                        disabled={submitting}
                        onChange={(partial) =>
                          setLine(line.service_id, (l) => ({ ...l, ...partial }))
                        }
                      />
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {visibleEnabledCount > 0 ? (
          <div className="banner banner-info">
            <span>
              <b>{visibleEnabledCount}</b> {visibleEnabledCount === 1 ? 'خدمة' : 'خدمات'} ضمن هذه
              الباقة. عند حفظها يمكن ربطها بأي عميل.
            </span>
          </div>
        ) : null}

        {error ? (
          <div className="banner banner-error" role="alert">
            {error}
          </div>
        ) : null}
      </div>
    </Drawer>
  );
}
